<template>
  <v-container grid-list-md class='page-container'>
    <v-layout class="no-print">
      <v-flex sm12 my-5 pa-3 d-block elevation-9>
        <h1>{{ $vuetify.t('$vuetify.AdminPassList.title') }}</h1>

        <v-autocomplete
          name="group"
          prepend-icon="group"
          :loading="isLoading"
          :items="ideaSpaceFilterOptions"
          :label="$vuetify.t('$vuetify.AdminPassList.selectSpaceLabel')"
          v-model="selectedIdeaSpace"
          @change="filter()"
        />
        <v-autocomplete
          multiple
          name="roles"
          prepend-icon="key"
          :loading="isLoading"
          :items="rolesSpaceFilterOptions"
          :label="$vuetify.t('$vuetify.AdminPassList.selectRoleLabel')"
          v-model="selectedRoles"
          @change="filter()"
          :placeholder="$vuetify.t('$vuetify.AdminPassList.selectRolePlaceholder')"
        />
        <v-btn
          :disabled="isLoading"
          color="primary"
          @click="printPage"
        >{{ $vuetify.t('$vuetify.AdminPassList.buttonPrint') }}
        </v-btn>
        <v-btn to="/admin">{{ $vuetify.t('$vuetify.AdminPassList.buttonBack') }}</v-btn>
      </v-flex>
    </v-layout>
    <v-layout row wrap class="print">
      <v-flex v-for="(user, i) in filteredUsers" :key="user.username" class="user-card">
        <v-card tile flat>
          <v-card-text>
            <table>
              <tr>
                <td>
                  <h2>{{ user.first_name }} {{ user.last_name }}</h2>
                  <ul>
                    <li>
                      <strong>{{ $vuetify.t('$vuetify.AdminPassList.labelUsername') }}:</strong>
                      <span class="monospace">{{ user.username }}</span>
                    </li>
                    <li v-if="user.config.temp_password == null">
                      <em>{{ $vuetify.t('$vuetify.AdminPassList.labelNoPassword') }}</em>
                    </li>
                    <li v-else>
                      <strong>{{ $vuetify.t('$vuetify.AdminPassList.labelPassword') }}:</strong>
                      <span class="monospace">{{ user.config.temp_password }}</span>
                    </li>
                  </ul>
                </td>
              </tr>
            </table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import api from '@/api'
import {getUserRoles} from '../utils/userRoles'

export default {
  name: 'PasswordList',
  data: function () {
    return {
      isLoading: true,
      users: [],
      ideaSpaces: [],
      roles: [],
      selectedIdeaSpace: null,
      selectedRoles: [],
      filteredUsers: []
    }
  },

  beforeMount: async function () {
    this.getUsers()
    this.getIdeaSpaces()
    await this.filter()
    this.getRoles()
  },

  computed: {
    ideaSpaceFilterOptions: function () {
      return [{
        text: 'Alle Benutzer',
        value: null
      },
      ...this.ideaSpaces
      ]
    },
    rolesSpaceFilterOptions: function () {
      return this.roles
    }
  },

  methods: {
    printPage: function () {
      window.print()
    },
    getIdeaSpaces: function () {
      const schoolId = this.$store.getters.selected_school
      api.ideaSpace.getIdeaSpaces(schoolId).then((res) => {
        this.ideaSpaces = res.data.map(sp => ({
          text: sp.title,
          value: sp.id
        }))
      })
    },
    getRoles: function () {
      const schoolId = this.$store.getters.selected_school
      getUserRoles(schoolId).then((res) => {
        this.roles = res.map(rl => ({
          text: rl.name,
          value: rl.name.toLowerCase().replace(/\s/g, '')
        }))
      })
    },
    getUsers: function () {
      const parseConfig = entry => Object.assign({}, entry, {
        config: JSON.parse(entry.config)
      })

      // Roles are returned from Postgrest as a composite value, which means
      // they're strings that need to be unpacked
      const parseRoles = entry => Object.assign({}, entry, {
        roles: entry.roles === null ? [] : entry.roles.map(val => {
          // Remove outer parentheses and split into
          // role_name, space_id, space_name
          let rv = [val['f1'].toLowerCase().replace(/\s/g, ''), val['f2'], val['f3']]

          return rv
        })
      })

      // Insert a boolean field that indicates whether the config field
      // contains a temporary password
      const checkTempPassword = entry => Object.assign({}, entry, {
        hasSetPassword: entry.config.temp_password == null
      })

      var schoolId = null
      if (this.$store.getters.selected_school) {
        schoolId = this.$store.getters.selected_school
      }
      api.user.getListing(schoolId)
        .then((res) => {
          this.users = res.data
            .map(parseConfig)
            .map(parseRoles)
            .map(checkTempPassword)
          this.filter()
          this.isLoading = false
        })
    },
    filter: async function () {
      const self = this
      let filteredUsers = this.users

      async function filterBySpace (spaceId) {
        if (!spaceId) {
          return
        }
        const result = await api.ideaSpace.getUsers(self.$store.getters.selected_school, spaceId)
        const spaceMembers = result.data
        filteredUsers = filteredUsers
          .filter(user => spaceMembers.some(spaceMember => spaceMember.user_id === user.id))
      }

      function filterByRole (roleIds, ideaSpace) {
        if (!roleIds || roleIds.length === 0) {
          return
        }

        filteredUsers = filteredUsers
          .filter(user => ideaSpace ? user.roles.some(role => role[1] === ideaSpace && roleIds.includes(role[0])) : true)
          .filter(user => user.roles.some(role => roleIds.includes(role[0])))
      }

      await filterBySpace(this.selectedIdeaSpace)
      filterByRole(this.selectedRoles, this.selectedIdeaSpace)

      this.filteredUsers = filteredUsers
    }
  }
}
</script>

<style lang="scss">
ul {
  list-style-type: none;
}

.page-container {
  max-width: 960px;
}

.monospace {
  font-family: "Lucida Console", Monaco, monospace;
}

.user-card {
  width: 50%;
  height: 9em;
  page-break-inside: avoid;
  position: relative;
  display: block;
  border: 1px dashed #999;
}

@media print {
  .v-toolbar,
  .no-print,
  footer {
    display: none !important;
  }
  body {
    overflow: visible !important;
  }
  .print {
    overflow: visible !important;
    height: 100% !important;
    position: relative !important;

    flex-wrap: unset !important;
    flex-direction: unset !important;
    display: block !important;
    margin: inherit !important;
  }
  .user-card {
    float: left;
  }

  * {
    overflow: visible !important;
  }
  .v-content,
  .container {
    padding: 0 !important;
    margin: 0;
  }
  .v-card-text {
    page-break-inside: avoid !important;
    position: relative;
  }

  .application--wrap {
    min-height: auto;
    background: none;
  }
}
</style>

